import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  PlayCircle,
  Save,
} from "lucide-react";
import {
  REASONING_EFFORTS,
  createExtractionConfig,
  getExtractionConfig,
  testExtractionConfig,
  updateExtractionConfig,
  validateExtractionConfig,
  type ExtractionConfigPayload,
  type ExtractionTestResponse,
  type ReasoningEffort,
} from "@/api/extraction";
import { ApiError } from "@/api/client";
import { Page } from "@/components/page";
import { useDebounced } from "@/lib/use-debounce";

const EMPTY: ExtractionConfigPayload = {
  name: "",
  enabled: false,
  model: "gpt-5.4-nano",
  reasoning_effort: "low",
  system_prompt: "",
  phone_fallback_prompt: "",
  verify_model: "gpt-5.4-nano",
  verify_reasoning_effort: "low",
  verify_text_limit: 6000,
};

export function ExtractionEditPage() {
  const { configId } = useParams({ strict: false }) as { configId: string };
  const isNew = configId === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const remote = useQuery({
    queryKey: ["extraction-config", configId],
    queryFn: () => getExtractionConfig(configId),
    enabled: !isNew,
  });

  const [draft, setDraft] = useState<ExtractionConfigPayload>(EMPTY);

  useEffect(() => {
    if (!remote.data) return;
    setDraft({
      name: remote.data.name,
      enabled: remote.data.enabled,
      model: remote.data.model,
      reasoning_effort: remote.data.reasoning_effort,
      system_prompt: remote.data.system_prompt,
      phone_fallback_prompt: remote.data.phone_fallback_prompt,
      verify_model: remote.data.verify_model,
      verify_reasoning_effort: remote.data.verify_reasoning_effort,
      verify_text_limit: remote.data.verify_text_limit,
    });
  }, [remote.data]);

  const debounced = useDebounced(draft, 350);
  const validation = useQuery({
    queryKey: ["extraction-config", "validate", debounced],
    queryFn: async () => {
      try {
        return await validateExtractionConfig(debounced);
      } catch (e) {
        if (e instanceof ApiError) return e;
        throw e;
      }
    },
    enabled: !!debounced.name,
    staleTime: 1_000,
    retry: false,
  });

  const validationError = useMemo(() => {
    const v = validation.data;
    if (v instanceof ApiError) return formatApiError(v);
    return null;
  }, [validation.data]);

  const save = useMutation({
    mutationFn: () =>
      isNew
        ? createExtractionConfig(draft)
        : updateExtractionConfig(configId, draft),
    onSuccess: (r) => {
      toast.success(isNew ? "Config created" : "Config saved");
      qc.invalidateQueries({ queryKey: ["extraction-configs"] });
      qc.invalidateQueries({ queryKey: ["extraction-config", configId] });
      if (isNew && "id" in r) {
        navigate({
          to: "/extraction/$configId",
          params: { configId: r.id as string },
        });
      }
    },
  });

  const [testResult, setTestResult] = useState<ExtractionTestResponse | null>(
    null,
  );
  const test = useMutation({
    mutationFn: () => testExtractionConfig(draft),
    onSuccess: (r) => {
      setTestResult(r);
      if (r.ok) toast.success("Trial extraction succeeded");
      else toast.error(`Trial failed: ${r.error ?? "unknown"}`);
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const canSave =
    !validationError && !!draft.name && !!draft.model && !save.isPending;

  return (
    <Page
      title={isNew ? "New extraction config" : remote.data?.name ?? "Extraction config"}
      actions={
        <>
          <Link to="/extraction" className="btn-ghost text-sm">
            <ArrowLeft className="h-4 w-4" /> All configs
          </Link>
          <button
            className="btn-ghost text-sm"
            onClick={() => test.mutate()}
            disabled={!canSave || test.isPending}
            title="Run a live OpenAI extraction against a small bundled Impressum"
          >
            <PlayCircle className="h-4 w-4" /> {test.isPending ? "Testing…" : "Test"}
          </button>
          <button
            className="btn-primary"
            onClick={() => save.mutate()}
            disabled={!canSave}
          >
            <Save className="h-4 w-4" /> Save
          </button>
        </>
      }
    >
      <div className="grid gap-4 lg:grid-cols-[24rem_1fr]">
        <section className="card p-4 space-y-3">
          <h2 className="text-sm font-semibold">Settings</h2>
          <div>
            <label className="label">Name</label>
            <input
              className="input"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Model</label>
              <input
                className="input font-mono text-xs"
                value={draft.model}
                onChange={(e) => setDraft({ ...draft, model: e.target.value })}
                placeholder="gpt-5-mini"
              />
            </div>
            <div>
              <label className="label">Verify model</label>
              <input
                className="input font-mono text-xs"
                value={draft.verify_model}
                onChange={(e) =>
                  setDraft({ ...draft, verify_model: e.target.value })
                }
                placeholder="gpt-5-mini"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <EffortSelect
              label="Reasoning effort"
              value={draft.reasoning_effort}
              onChange={(v) => setDraft({ ...draft, reasoning_effort: v })}
            />
            <EffortSelect
              label="Verify reasoning effort"
              value={draft.verify_reasoning_effort}
              onChange={(v) =>
                setDraft({ ...draft, verify_reasoning_effort: v })
              }
            />
          </div>
          <p className="text-xs text-muted">
            Effort sent on every extraction / verifier call.{" "}
            <code>gpt-5-mini</code> accepts <code>minimal</code>; nano-class
            models reject it and need <code>low</code> or higher.
          </p>
          <div>
            <label className="label">Verify text limit (chars)</label>
            <input
              type="number"
              className="input"
              value={draft.verify_text_limit}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  verify_text_limit: Number(e.target.value) || 0,
                })
              }
            />
            <p className="mt-1 text-xs text-muted">
              Cap on cleaned page text fed to the LLM-verifier. 6000 chars at
              <code className="ml-1">minimal</code> reasoning is the eval baseline.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
            />
            Active (only one row can be active at a time)
          </label>
        </section>

        <section className="space-y-3 min-w-0">
          <ValidationBanner
            error={validationError}
            ok={!validationError && !!draft.name}
          />
          <PromptCard
            label="System prompt (Impressum → LeadData)"
            value={draft.system_prompt}
            onChange={(v) => setDraft({ ...draft, system_prompt: v })}
            hint={
              <>
                Reference the JSON example fields with{" "}
                <code>{`{{ json_example }}`}</code> and{" "}
                <code>{`{{ json_fields }}`}</code>. Both are rendered at
                run-snapshot time from the code-side <code>LeadData</code>{" "}
                schema.
              </>
            }
          />
          <PromptCard
            label="Phone fallback prompt (banner phone recovery)"
            value={draft.phone_fallback_prompt}
            onChange={(v) => setDraft({ ...draft, phone_fallback_prompt: v })}
            hint={
              <>
                Must contain the load-bearing precision guards: the words{" "}
                <code>Drittanbieter</code>, <code>Schlichtungsstelle</code>,{" "}
                <code>Partialnummern</code>, <code>Platzhalter</code>,{" "}
                <code>phone=null</code>, and <code>Ziffern</code>. The validate
                endpoint refuses to save a prompt that drops any of them.
              </>
            }
          />
          {testResult ? <TestResultCard result={testResult} /> : null}
        </section>
      </div>
    </Page>
  );
}

function EffortSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: ReasoningEffort;
  onChange: (v: ReasoningEffort) => void;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <select
        className="input font-mono text-xs"
        value={value}
        onChange={(e) => onChange(e.target.value as ReasoningEffort)}
      >
        {REASONING_EFFORTS.map((eff) => (
          <option key={eff} value={eff}>
            {eff}
          </option>
        ))}
      </select>
    </div>
  );
}

function PromptCard({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint: React.ReactNode;
}) {
  return (
    <div className="card p-4 space-y-2">
      <label className="label">{label}</label>
      <textarea
        className="input min-h-[14rem] font-mono text-xs leading-relaxed"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <p className="text-xs text-muted leading-relaxed">{hint}</p>
    </div>
  );
}

function TestResultCard({ result }: { result: ExtractionTestResponse }) {
  return (
    <div className="card p-4">
      <h3 className="text-sm font-semibold mb-2">
        Trial result {result.ok ? "" : "(failed)"}
      </h3>
      {result.error ? (
        <pre className="text-xs text-danger whitespace-pre-wrap break-words">
          {result.error}
        </pre>
      ) : (
        <pre className="text-xs whitespace-pre-wrap break-words bg-subtle p-2 rounded-md overflow-auto">
          {JSON.stringify(result.lead, null, 2)}
        </pre>
      )}
      {result.gpt_meta ? (
        <p className="mt-2 text-xs text-muted">
          model={result.gpt_meta.model} · prompt_tokens=
          {result.gpt_meta.prompt_tokens} · completion_tokens=
          {result.gpt_meta.completion_tokens}
        </p>
      ) : null}
    </div>
  );
}

function ValidationBanner({
  error,
  ok,
}: {
  error: string | null;
  ok: boolean;
}) {
  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
        <pre className="text-xs whitespace-pre-wrap break-words font-mono">
          {error}
        </pre>
      </div>
    );
  }
  if (!ok) return null;
  return (
    <div className="flex items-center gap-2 rounded-md border border-success/40 bg-success/10 p-2 text-xs text-success">
      <Check className="h-4 w-4" /> Validated.
    </div>
  );
}

function formatApiError(e: ApiError): string {
  if (Array.isArray(e.detail)) {
    return e.detail
      .map((d: { loc?: string[]; msg: string }) =>
        `${(d.loc ?? []).join(".")}: ${d.msg}`,
      )
      .join("\n");
  }
  return typeof e.detail === "string" ? e.detail : e.message;
}
